(function() {
class PartnerApplicationForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    var form = this.querySelector('form');
    if (!form) return;
    this.initialized = true;
    this.controller = new AbortController();
    var signal = this.controller.signal;

    var submitBtn = this.querySelector('[type=submit]');
    var msgEl = this.querySelector('[role=status]');
    var root = this;
    var captchaEl = this.querySelector('[data-partner-captcha]');
    var sitekey = captchaEl.dataset.sitekey;
    var captchaToken = '';
    var widgetId = null;
    var widgetSize = null;
    var submitting = false;
    var endpoint = 'https://form-intake-api-production.up.railway.app/api/submissions';

    function resetCaptcha() {
      captchaToken = '';
      if (widgetId !== null && window.turnstile) window.turnstile.reset(widgetId);
    }

    function renderCaptcha() {
      if (!sitekey || widgetId !== null) return;
      loadPartnerTurnstile().then(function() {
        if (signal.aborted || !root.isConnected || widgetId !== null) return;
        widgetSize = captchaEl.clientWidth < 300 ? 'compact' : 'flexible';
        widgetId = window.turnstile.render(captchaEl, {
          sitekey: sitekey,
          action: 'cheeky-partner-application',
          size: widgetSize,
          callback: function(token) { captchaToken = token; },
          'expired-callback': function() { captchaToken = ''; },
          'timeout-callback': function() { captchaToken = ''; },
          'error-callback': function() {
            captchaToken = '';
            showMessage('The security check could not load. Please refresh the page and try again.', 'error');
          }
        });
        root.widgetId = widgetId;
      }).catch(function() {
        if (signal.aborted) return;
        showMessage('The security check could not load. Please try again or refresh the page.', 'error');
      });
    }
    window.addEventListener('resize', function() {
      var size = captchaEl.clientWidth < 300 ? 'compact' : 'flexible';
      if (widgetId !== null && size !== widgetSize) {
        window.turnstile.remove(widgetId);
        widgetId = null;
        captchaToken = '';
        renderCaptcha();
      }
    }, { signal: signal });
    renderCaptcha();
    submitBtn.disabled = false;

    /* ── Format numeric inputs with commas as the user types ── */
    function formatWithCommas(value) {
      var digits = (value || '').replace(/\D/g, '');
      if (!digits) return '';
      return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function stripCommas(value) {
      return (value || '').replace(/,/g, '');
    }

    form.querySelectorAll('input[data-format="number"]').forEach(function(input) {
      input.addEventListener('input', function() {
        var caret = input.selectionStart;
        var before = input.value;
        var formatted = formatWithCommas(before);
        if (formatted === before) return;
        input.value = formatted;
        /* keep caret roughly where it was */
        var diff = formatted.length - before.length;
        var pos = Math.max(0, (caret || 0) + diff);
        try { input.setSelectionRange(pos, pos); } catch (e) {}
      }, { signal: signal });
    });

    function showMessage(text, type) {
      msgEl.textContent = text;
      msgEl.className = 'partner-form-section__message partner-form-section__message--' + type;
      msgEl.hidden = false;
    }

    function isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      if (submitting) return;
      msgEl.hidden = true;

      var data = new FormData(form);
      var name = (data.get('name') || '').trim();
      var email = (data.get('email') || '').trim();

      if (!name) {
        showMessage('Please enter your name.', 'error');
        return;
      }
      if (!email || !isValidEmail(email)) {
        showMessage('Please enter a valid email address.', 'error');
        return;
      }

      if (sitekey && !captchaToken) {
        renderCaptcha();
        showMessage('Please complete the security check before submitting.', 'error');
        return;
      }

      var payload = {
        formId: 'cheeky-partner-application',
        captchaProvider: sitekey ? 'turnstile' : undefined,
        captchaToken: captchaToken || undefined,
        source: 'shopify_embed',
        responses: {
          name: name,
          email: email,
          youtubeUrl: (data.get('youtubeUrl') || '').trim(),
          twitterUrl: (data.get('twitterUrl') || '').trim(),
          twitchUrl: (data.get('twitchUrl') || '').trim(),
          tiktokUrl: (data.get('tiktokUrl') || '').trim(),
          instagramUrl: (data.get('instagramUrl') || '').trim(),
          youtubeSubscribers: stripCommas(data.get('youtubeSubscribers') || '').trim(),
          instagramFollowers: stripCommas(data.get('instagramFollowers') || '').trim(),
          twitterFollowers: stripCommas(data.get('twitterFollowers') || '').trim(),
          tiktokFollowers: stripCommas(data.get('tiktokFollowers') || '').trim(),
          twitchCcv: stripCommas(data.get('twitchCcv') || '').trim()
        },
        submittedAt: new Date().toISOString(),
        pageUrl: window.location.href,
        honeypot: data.get('honeypot') || ''
      };

      submitting = true;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function(response) {
          if (!response.ok) {
            var error = new Error('Submission failed');
            error.status = response.status;
            return response.json().catch(function() { return {}; }).then(function(body) {
              error.code = body.code;
              throw error;
            });
          }
          return response.json();
        })
        .then(function() {
          showMessage('Thanks! Your application has been submitted. We will be in touch soon.', 'success');
          form.reset();
          resetCaptcha();
          submitting = false;
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit Application';
        })
        .catch(function(err) {
          resetCaptcha();
          submitting = false;
          var message = 'Something went wrong. Please try again or email us directly.';
          if (err.status === 429) message = 'Too many attempts. Please wait 15 minutes before trying again.';
          else if (err.code === 'captcha_failed') message = 'Verification failed. Please complete the security check and try again.';
          showMessage(message, 'error');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit Application';
        });
    }, { signal: signal });
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    this.initialized = false;
    if (this.widgetId !== undefined && window.turnstile) window.turnstile.remove(this.widgetId);
    this.widgetId = undefined;
  }
}

function loadPartnerTurnstile() {
  if (window.turnstile) return Promise.resolve();
  if (window.cheekyPartnerTurnstile) return window.cheekyPartnerTurnstile;
  window.cheekyPartnerTurnstile = new Promise(function(resolve, reject) {
    var script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    var timeout = setTimeout(function() { fail(); }, 15000);
    function fail() {
      clearTimeout(timeout);
      script.remove();
      window.cheekyPartnerTurnstile = null;
      reject(new Error('Security check unavailable'));
    }
    script.onload = function() {
      clearTimeout(timeout);
      if (window.turnstile) resolve();
      else fail();
    };
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return window.cheekyPartnerTurnstile;
}

if (!customElements.get('partner-application-form')) {
  customElements.define('partner-application-form', PartnerApplicationForm);
}
})();
